import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, CardHeader, Row, Col, Button, ButtonGroup } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { DatosSesion } from "../../Components/Common/DatosSesion";
import { getSolicitudesCreadas } from "../../helpers/backend_helper";
import WidgetLoader from "./WidgetLoader";

const SolicitudesCreadas = React.memo(({ fechaInicio, fechaFin, codigoProveedor, aseguradora }) => {
  const [agrupacion, setAgrupacion] = useState("dia");
  const [tipoSolicitud, setTipoSolicitud] = useState("total");
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(false);
  const primeraVezRef = useRef(true);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Validar que tengamos fechas
    if (!fechaInicio || !fechaFin) {
      return;
    }
    
    // Si es la primera vez y es proveedor sin código, esperar
    if (primeraVezRef.current) {
      const perfil = DatosSesion("perfil") || "";
      const esAdministrador = perfil === "Administrador" || perfil === "MyHealth";
      
      if (!esAdministrador && codigoProveedor === null) {
        return;
      }
      
      primeraVezRef.current = false;
    }
    
    // Cancelar timeout anterior si existe
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Debounce: esperar 100ms antes de ejecutar
    timeoutRef.current = setTimeout(() => {
      fetchSolicitudesCreadas();
    }, 100);
    
    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [fechaInicio, fechaFin, codigoProveedor, aseguradora, agrupacion, tipoSolicitud]);

  const fetchSolicitudesCreadas = async () => {
    setLoading(true);
    try {
      // Formatear fechas como strings ISO
      const params = {
        fecha_inicio: typeof fechaInicio === 'string' ? fechaInicio : fechaInicio?.toISOString?.() || fechaInicio,
        fecha_fin: typeof fechaFin === 'string' ? fechaFin : fechaFin?.toISOString?.() || fechaFin,
        agrupacion: agrupacion,
        tipo_solicitud: tipoSolicitud,
      };

      if (codigoProveedor && codigoProveedor !== "null" && codigoProveedor !== "") {
        params.codigo_proveedor = codigoProveedor;
      }

      if (aseguradora) {
        params.aseguradora = aseguradora;
      }

      const response = await getSolicitudesCreadas(params);

      if (response.data && response.data.data) {
        setDatos(response.data.data);
      }
    } catch (error) {
      console.error("Error al obtener solicitudes creadas:", error);
      if (error.response) {
        console.error("Response error:", error.response.status, error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const getChartOptions = () => {
    if (!datos || !datos.series || datos.series.length === 0) {
      return {};
    }

    return {
      chart: {
        type: "line",
        height: 350,
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
        zoom: {
          enabled: true,
        },
      },
      stroke: {
        width: 3,
        curve: "smooth",
      },
      colors: ["#405189", "#0ab39c", "#f7b84b", "#f06548", "#299cdb"],
      xaxis: {
        type: "datetime",
        labels: {
          format: agrupacion === "mes" ? "MMM yyyy" : "dd MMM",
          datetimeUTC: false,
        },
      },
      yaxis: {
        title: {
          text: "Solicitudes",
        },
        labels: {
          formatter: (val) => val ? Math.round(val) : 0,
        },
      },
      tooltip: {
        x: {
          format: agrupacion === "mes" ? "MMM yyyy" : "dd MMM yyyy",
        },
        y: {
          formatter: (val) => val ? Math.round(val) : 0,
        },
      },
      legend: {
        position: "top",
        horizontalAlign: "left",
      },
      grid: {
        borderColor: "#f1f1f1",
      },
      markers: {
        size: 4,
        hover: {
          size: 6,
        },
      },
    };
  };

  const getChartSeries = () => {
    if (!datos || !datos.series) {
      return [];
    }

    return datos.series.map((serie) => ({
      name: serie.name,
      data: serie.data.map((point) => ({
        x: new Date(point.x + "T00:00:00").getTime(),
        y: point.y,
      })),
    }));
  };

  return (
    <Card className="card-height-100">
      <CardHeader className="d-flex justify-content-between align-items-center">
        <h4 className="card-title mb-0">Solicitudes creadas</h4>
        <div className="d-flex gap-2">
          <ButtonGroup size="sm">
            <Button
              color={agrupacion === "dia" ? "primary" : "light"}
              onClick={() => setAgrupacion("dia")}
            >
              Día
            </Button>
            <Button
              color={agrupacion === "semana" ? "primary" : "light"}
              onClick={() => setAgrupacion("semana")}
            >
              Semana
            </Button>
            <Button
              color={agrupacion === "mes" ? "primary" : "light"}
              onClick={() => setAgrupacion("mes")}
            >
              Mes
            </Button>
          </ButtonGroup>
        </div>
      </CardHeader>
      <CardBody>
        <div className="mb-3">
          <ButtonGroup size="sm" className="w-100">
            <Button
              color={tipoSolicitud === "total" ? "primary" : "outline-primary"}
              onClick={() => setTipoSolicitud("total")}
              className="flex-fill"
            >
              Total
            </Button>
            <Button
              color={tipoSolicitud === "Cronica" ? "success" : "outline-success"}
              onClick={() => setTipoSolicitud("Cronica")}
              className="flex-fill"
            >
              Crónicas
            </Button>
            <Button
              color={tipoSolicitud === "Aguda" ? "warning" : "outline-warning"}
              onClick={() => setTipoSolicitud("Aguda")}
              className="flex-fill"
            >
              Agudas
            </Button>
          </ButtonGroup>
        </div>

        {loading ? (
          <div className="placeholder-glow mt-4">
             {/* Gráfica Skeleton */}
             <div className="w-100 placeholder mb-4" style={{height: '350px', borderRadius: '4px'}}></div>
             
             {/* Stats Skeleton */}
             <Row className="mt-4 text-center g-3">
               {[1, 2, 3].map(k => (
                 <Col xs={4} key={k}>
                   <div className="border rounded p-3">
                     <span className="placeholder col-6 mb-2"></span>
                     <span className="placeholder col-8 d-block mx-auto"></span>
                   </div>
                 </Col>
               ))}
             </Row>
          </div>
        ) : datos && datos.series && datos.series.length > 0 ? (
          <>
            <ReactApexChart
              options={getChartOptions()}
              series={getChartSeries()}
              type="line"
              height={350}
            />

            <Row className="mt-4 text-center g-3">
              <Col xs={4}>
                <div className="border rounded p-3">
                  <p className="text-muted mb-1 fs-13">Promedio General</p>
                  <h5 className="mb-0 text-primary">
                    {datos.estadisticas?.promedio_general || 0}
                  </h5>
                </div>
              </Col>
              <Col xs={4}>
                <div className="border rounded p-3">
                  <p className="text-muted mb-1 fs-13">Máximo</p>
                  <h5 className="mb-0 text-success">
                    {datos.estadisticas?.max || 0}
                  </h5>
                </div>
              </Col>
              <Col xs={4}>
                <div className="border rounded p-3">
                  <p className="text-muted mb-1 fs-13">Mínimo</p>
                  <h5 className="mb-0 text-danger">
                    {datos.estadisticas?.min || 0}
                  </h5>
                </div>
              </Col>
            </Row>
          </>
        ) : (
          <div className="text-center py-5">
            <i className="ri-bar-chart-line fs-1 text-muted"></i>
            <p className="text-muted mt-3">No hay datos disponibles para el período seleccionado</p>
          </div>
        )}
      </CardBody>
    </Card>
  );
});

export default SolicitudesCreadas;
